import React, { useState, useEffect } from 'react';
import { debugLog, errorLog } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, UserCheck, UserX, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';

interface User {
  id: string;
  email: string;
  username?: string;
  fullName: string;
  walletAddress?: string;
  role: 'user' | 'admin';
  isActive: boolean;
  createdAt?: string;
  avatarUrl?: string;
  totalCoinsEarned?: number;
  lastActive?: string;
}

const getInitials = (name: string) =>
  name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.users.getAllUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      debugLog('Activating user:', userId);
      const response = await apiClient.users.activateUser(userId);
      debugLog('Activate response:', response);

      if (response.success) {
        toast.success('User activated successfully');
        await fetchUsers();
      } else {
        errorLog('Activation failed:', response);
        toast.error(response.message || 'Failed to activate user');
      }
    } catch (error: any) {
      errorLog('Failed to activate user:', error);

      if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid request');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to activate user');
      }
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      debugLog('Deactivating user:', userId);
      const response = await apiClient.users.deactivateUser(userId);
      debugLog('Deactivate response:', response);

      if (response.success) {
        toast.success('User deactivated successfully');
        await fetchUsers();
      } else {
        errorLog('Deactivation failed:', response);
        toast.error(response.message || 'Failed to deactivate user');
      }
    } catch (error: any) {
      errorLog('Failed to deactivate user:', error);

      if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid user ID format');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to deactivate user');
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.isActive) ||
                         (statusFilter === 'inactive' && !user.isActive);

    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-4xl font-bold text-white">User Management</h1>
          <p className="text-lg text-white mt-1">Manage user accounts and permissions</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users size={20} />
              Users ({filteredUsers.length})
            </h3>
            <button
              onClick={fetchUsers}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Refresh
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No users found</p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-4 sm:p-6 hover:bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">

                  {/* Left: Avatar + Identity */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex-shrink-0">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.fullName}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                          {getInitials(user.fullName)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{user.fullName}</p>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Joined: {formatDate(user.createdAt)}</p>
                    </div>
                  </div>

                  {/* Middle: Stat chips */}
                  <div className="flex flex-wrap items-center gap-2 sm:flex-col sm:items-start sm:gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-50 text-yellow-800 text-xs font-medium border border-yellow-200">
                      🪙 {(user.totalCoinsEarned ?? 0).toFixed(2)} HEP
                    </span>
                    {user.walletAddress ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal-50 text-teal-700 text-xs font-medium border border-teal-200">
                        ✓ {formatAddress(user.walletAddress)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-500 text-xs font-medium">
                        No wallet
                      </span>
                    )}
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 text-gray-500 text-xs border border-gray-200">
                      Active: {user.lastActive ? formatDate(user.lastActive) : 'Never'}
                    </span>
                  </div>

                  {/* Right: Badges + Action */}
                  <div className="flex sm:flex-col items-center sm:items-end gap-2">
                    <div className="flex gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'admin'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {user.isActive ? (
                      <button
                        onClick={() => handleDeactivateUser(user.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 text-xs font-medium touch-manipulation whitespace-nowrap"
                      >
                        <UserX size={14} />
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateUser(user.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 text-xs font-medium touch-manipulation whitespace-nowrap"
                      >
                        <UserCheck size={14} />
                        Activate
                      </button>
                    )}
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
